import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import {
  GitBranch,
  Wallet,
  HandCoins,
  Users,
  FileBarChart,
  LayoutDashboard,
  MessageCircle,
  Shield,
  Car,
  Receipt,
  Layers,
} from 'lucide-react';
import { PRODUCT_NAME } from '../../lib/tenant';

type Module = {
  icon: LucideIcon;
  title: string;
  desc: string;
  stat: string;
  color: string;
  ring: string;
  glow: string;
  accent: string;
};

const MODULES: Module[] = [
  {
    icon: GitBranch,
    title: 'Branch ledgers',
    desc: 'HQ + every shop book in sync',
    stat: '∞ books',
    color: 'text-emerald-300',
    ring: 'border-emerald-500/35 bg-emerald-500/12',
    glow: 'shadow-emerald-500/25',
    accent: 'from-emerald-500/25 via-transparent to-transparent',
  },
  {
    icon: Wallet,
    title: 'Hire-purchase',
    desc: 'Guided HP origination & EMI',
    stat: 'EMI ledger',
    color: 'text-teal-300',
    ring: 'border-teal-500/35 bg-teal-500/12',
    glow: 'shadow-teal-500/25',
    accent: 'from-teal-500/25 via-transparent to-transparent',
  },
  {
    icon: HandCoins,
    title: 'Hand loans',
    desc: 'Informal lending, tracked',
    stat: 'Recovery',
    color: 'text-cyan-300',
    ring: 'border-cyan-500/35 bg-cyan-500/12',
    glow: 'shadow-cyan-500/25',
    accent: 'from-cyan-500/25 via-transparent to-transparent',
  },
  {
    icon: Users,
    title: 'Customer 360',
    desc: 'Full borrower profile',
    stat: 'Mobile-first',
    color: 'text-sky-300',
    ring: 'border-sky-500/35 bg-sky-500/12',
    glow: 'shadow-sky-500/25',
    accent: 'from-sky-500/25 via-transparent to-transparent',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    desc: 'Meta API payment nudges',
    stat: 'Auto reminders',
    color: 'text-green-300',
    ring: 'border-green-500/35 bg-green-500/12',
    glow: 'shadow-green-500/25',
    accent: 'from-green-500/25 via-transparent to-transparent',
  },
  {
    icon: FileBarChart,
    title: 'Day reports',
    desc: 'Period KPIs & exports',
    stat: 'PDF + Excel',
    color: 'text-amber-300',
    ring: 'border-amber-500/35 bg-amber-500/12',
    glow: 'shadow-amber-500/20',
    accent: 'from-amber-500/20 via-transparent to-transparent',
  },
  {
    icon: LayoutDashboard,
    title: 'Master dashboard',
    desc: 'Owner command center',
    stat: 'Live charts',
    color: 'text-emerald-300',
    ring: 'border-emerald-500/35 bg-emerald-500/12',
    glow: 'shadow-emerald-500/25',
    accent: 'from-emerald-500/25 via-transparent to-transparent',
  },
  {
    icon: Car,
    title: 'Consultancy',
    desc: 'Vehicle sales desk',
    stat: 'Profit roll-up',
    color: 'text-teal-200',
    ring: 'border-teal-500/35 bg-teal-500/12',
    glow: 'shadow-teal-500/20',
    accent: 'from-teal-400/20 via-transparent to-transparent',
  },
  {
    icon: Receipt,
    title: 'Expenses',
    desc: 'Operational spend control',
    stat: 'Net position',
    color: 'text-slate-300',
    ring: 'border-white/15 bg-white/5',
    glow: 'shadow-white/10',
    accent: 'from-white/10 via-transparent to-transparent',
  },
  {
    icon: Shield,
    title: 'Tenancy & RBAC',
    desc: 'Isolated org access',
    stat: 'Per module',
    color: 'text-slate-300',
    ring: 'border-white/15 bg-white/5',
    glow: 'shadow-white/10',
    accent: 'from-white/10 via-transparent to-transparent',
  },
];

function OrbitNode({ module, index, total, active }: { module: Module; index: number; total: number; active: boolean }) {
  const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
  const radius = 42;
  const x = 50 + Math.cos(angle) * radius;
  const y = 50 + Math.sin(angle) * radius;

  return (
    <motion.div
      className="absolute z-10 hidden lg:block"
      style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.08 * index, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ scale: 1.08, zIndex: 20 }}
    >
      <div
        className={`group w-[148px] rounded-2xl border border-white/10 bg-[#0a0f1a]/90 p-3.5 backdrop-blur-xl transition-all duration-300 hover:border-white/20 ${
          active ? `shadow-lg ${module.glow}` : ''
        }`}
      >
        <div className={`mb-2.5 flex h-9 w-9 items-center justify-center rounded-xl border ${module.ring}`}>
          <module.icon className={`h-4 w-4 ${module.color}`} />
        </div>
        <p className="font-display text-xs font-semibold text-white">{module.title}</p>
        <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{module.desc}</p>
        <p className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${module.color}`}>{module.stat}</p>
      </div>
    </motion.div>
  );
}

function MobileSurfaceShowcase({ inView }: { inView: boolean }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const cards = el.querySelectorAll<HTMLElement>('[data-module-card]');
      if (!cards.length) return;
      const mid = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((card, i) => {
        const center = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(center - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      setActive(best);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (index: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelectorAll<HTMLElement>('[data-module-card]')[index];
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - 16, behavior: 'smooth' });
  };

  return (
    <div className="relative mx-auto mt-10 max-w-lg lg:hidden">
      {/* Atmosphere */}
      <div
        className="pointer-events-none absolute -inset-x-8 -top-6 h-64 bg-[radial-gradient(ellipse_at_50%_0%,rgba(16,185,129,0.22),transparent_65%)]"
        aria-hidden
      />

      {/* Hub — one composition, not a card grid */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.02] px-5 pb-5 pt-6"
      >
        <div
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-cyan-500/15 blur-3xl"
          aria-hidden
        />

        <div className="relative flex flex-col items-center text-center">
          <motion.div
            className="ef-platform-hub relative mb-4"
            animate={inView ? { scale: [1, 1.03, 1] } : {}}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="ef-platform-hub__ring" aria-hidden />
            <div className="relative flex h-[5.25rem] w-[5.25rem] flex-col items-center justify-center rounded-full border border-emerald-400/35 bg-[#060b14]/90 backdrop-blur-md">
              <Layers className="h-6 w-6 text-emerald-400" />
              <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-300/90">10</p>
            </div>
          </motion.div>

          <p className="font-display text-xl font-semibold tracking-tight text-white">One surface</p>
          <p className="mt-1.5 max-w-[16rem] text-[13px] leading-snug text-slate-400">
            {PRODUCT_NAME} — every desk connects to one tenant workspace
          </p>

          <div className="mt-5 flex w-full items-center justify-between gap-2 border-t border-white/8 pt-4">
            {['Ledgers', 'Lending', 'Collect', 'Reports'].map((label, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 8 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
                className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
              >
                <span className="h-1 w-8 rounded-full bg-gradient-to-r from-emerald-400/80 to-cyan-400/60" />
                <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Module rail */}
      <div className="relative mt-8">
        <div className="mb-3 flex items-end justify-between px-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Swipe modules</p>
          <p className="tabular-nums text-[11px] font-semibold text-emerald-400/90">
            {active + 1}
            <span className="text-slate-600"> / {MODULES.length}</span>
          </p>
        </div>

        <div className="ef-module-rail-mask relative">
          <div
            ref={scrollerRef}
            className="ef-module-rail flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pl-1 pr-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {MODULES.map((m, i) => (
              <motion.article
                key={m.title}
                data-module-card
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.05 * Math.min(i, 6), duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className={`relative w-[78%] max-w-[280px] shrink-0 snap-center overflow-hidden rounded-[1.35rem] border border-white/10 bg-[#080d18]/95 p-5 transition-[border-color,transform] duration-300 ${
                  active === i ? 'border-emerald-400/35' : ''
                }`}
              >
                <div
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${m.accent}`}
                  aria-hidden
                />
                <div className="relative">
                  <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border ${m.ring}`}>
                    <m.icon className={`h-5 w-5 ${m.color}`} />
                  </div>
                  <p className="font-display text-lg font-semibold tracking-tight text-white">{m.title}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{m.desc}</p>
                  <p className={`mt-4 text-[11px] font-bold uppercase tracking-[0.16em] ${m.color}`}>{m.stat}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-1.5">
          {MODULES.map((m, i) => (
            <button
              key={m.title}
              type="button"
              aria-label={`Show ${m.title}`}
              onClick={() => scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                active === i ? 'w-6 bg-emerald-400' : 'w-1.5 bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LaunchBentoGrid() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section className="relative overflow-hidden py-16 sm:py-20 lg:py-28">
      <div ref={ref} className="relative z-10 container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300 backdrop-blur-md sm:px-4 sm:text-[11px]">
            <Layers className="h-3.5 w-3.5" />
            Full platform
          </span>
          <h2 className="mt-5 font-display text-[1.85rem] font-semibold leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-5xl">
            Everything your finance shop runs on —{' '}
            <span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 bg-clip-text text-transparent">
              one surface
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-slate-400 sm:text-base sm:text-lg">
            {PRODUCT_NAME} isn&apos;t a point tool. Ten modules. One workspace — ledgers, lending, collections, and
            control.
          </p>
        </motion.div>

        {/* Desktop — orbital hub */}
        <div className="relative mx-auto mt-16 hidden max-w-5xl lg:block">
          <div className="relative mx-auto aspect-square w-full max-w-[720px]">
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" aria-hidden>
              <defs>
                <radialGradient id="orbit-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(16,185,129,0.15)" />
                  <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                </radialGradient>
              </defs>
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="rgba(52,211,153,0.12)"
                strokeWidth="0.15"
                strokeDasharray="1 2"
              />
              <circle cx="50" cy="50" r="28" fill="url(#orbit-glow)" />
              {MODULES.map((_, i) => {
                const angle = (i / MODULES.length) * Math.PI * 2 - Math.PI / 2;
                const x2 = 50 + Math.cos(angle) * 42;
                const y2 = 50 + Math.sin(angle) * 42;
                return (
                  <line
                    key={i}
                    x1="50"
                    y1="50"
                    x2={x2}
                    y2={y2}
                    stroke="rgba(52,211,153,0.2)"
                    strokeWidth="0.12"
                  />
                );
              })}
            </svg>

            <motion.div
              className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
              initial={{ opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="ef-platform-hub relative">
                <div className="ef-platform-hub__ring" aria-hidden />
                <div className="relative flex h-36 w-36 flex-col items-center justify-center rounded-full border border-emerald-500/30 bg-gradient-to-br from-emerald-500/20 via-[#0a0f1a] to-cyan-500/10 p-6 text-center shadow-[0_0_60px_-12px_rgba(16,185,129,0.45)] backdrop-blur-xl sm:h-40 sm:w-40">
                  <Layers className="mb-2 h-7 w-7 text-emerald-400" />
                  <p className="font-display text-sm font-bold text-white">One surface</p>
                  <p className="mt-1 text-[10px] font-medium text-slate-400">{PRODUCT_NAME}</p>
                  <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-emerald-400">10 modules</p>
                </div>
              </div>
            </motion.div>

            {MODULES.map((m, i) => (
              <OrbitNode key={m.title} module={m} index={i} total={MODULES.length} active={inView} />
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="mt-8 text-center text-sm text-slate-500"
          >
            Hover any module — every capability connects back to one tenant workspace
          </motion.p>
        </div>

        <MobileSurfaceShowcase inView={inView} />
      </div>
    </section>
  );
}
